<template>
  <v-row dense class="run-summary-cards" data-testid="run-summary-cards">
    <v-col cols="12" sm="6" md="3">
      <v-card outlined class="run-summary-card run-summary-card--result fill-height">
        <v-card-text class="py-2">
          <div class="overline">Result</div>
          <div class="d-flex align-center">
            <v-icon :color="result.color" class="mr-1">{{ result.icon }}</v-icon>
            <span class="subtitle-1 font-weight-medium" data-testid="run-result-label">
              {{ result.label }}
            </span>
          </div>
          <div class="caption grey--text text--darken-1">{{ humanLabel }}</div>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" sm="6" md="3">
      <v-card outlined class="run-summary-card run-summary-card--funnel fill-height">
        <v-card-text class="py-2">
          <div class="overline">Funnel</div>
          <div class="subtitle-1 font-weight-medium" data-testid="run-funnel">
            {{ funnel.episodes }} episodes → {{ funnel.alerts }} alerts →
            {{ funnel.subtracting === null ? '—' : funnel.subtracting }} ideas subtracting
          </div>
          <div class="caption grey--text text--darken-1">
            <template v-if="funnel.analysis_available">
              {{ funnel.ideas }} ideas · threshold {{ funnel.threshold_pct }}%
            </template>
            <template v-else>Coherence analysis not available for this run</template>
          </div>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" sm="6" md="3">
      <v-card outlined class="run-summary-card run-summary-card--duration fill-height">
        <v-card-text class="py-2">
          <div class="overline">Duration</div>
          <div class="subtitle-1 font-weight-medium" data-testid="run-duration-total">
            {{ formatMs(durations.total_ms) }}
          </div>
          <div v-if="durations.stages.length" class="caption grey--text text--darken-1">
            <span v-for="stage in durations.stages" :key="stage.name" class="mr-2">
              {{ stage.name }} {{ formatMs(stage.duration_ms) }}
            </span>
          </div>
          <div v-else class="caption grey--text text--darken-1">No per-stage timings</div>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" sm="6" md="3">
      <v-card outlined class="run-summary-card run-summary-card--delta fill-height">
        <v-card-text class="py-2">
          <div class="overline">vs previous run</div>
          <template v-if="delta">
            <div class="subtitle-1 font-weight-medium" data-testid="run-delta">
              alerts {{ formatDelta(delta.alerts) }} · ideas subtracting
              {{ formatDelta(delta.subtracting) }}
            </div>
            <div class="caption grey--text text--darken-1">
              Compared with {{ previousLabel }}
            </div>
          </template>
          <template v-else>
            <div class="subtitle-1 font-weight-medium">—</div>
            <div class="caption grey--text text--darken-1">
              {{ previousLoading ? 'Loading previous run…' : 'No previous succeeded run' }}
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import {
  RESULT_PRESENTATION,
  computeFunnel,
  deltaVsPrevious,
  formatDelta,
  formatMs,
  humanLabelText,
  resultVerdict,
  stageDurations,
} from '@/utils/runSummary';
import { runLabel } from '@/utils/runLabels';

/**
 * Four cards under the run header (M2 F5): engine result, detection funnel, wall time per
 * stage and the client-side delta against the previous SUCCEEDED run (its gestures payload is
 * loaded by the parent into a second store key).
 */
export default {
  name: 'RunSummaryCards',

  props: {
    /** metadata.run block of the run being viewed. */
    run: { type: Object, default: null },
    /** Gestures payload of the run being viewed (analysisData.getGestures). */
    gestures: { type: Object, default: null },
    /** Ledger doc of the previous SUCCEEDED run (null when there is none). */
    previousRun: { type: Object, default: null },
    /** Gestures payload of the previous run once loaded. */
    previousGestures: { type: Object, default: null },
    previousLoading: { type: Boolean, default: false },
    /** video_incoherence row of the source (human label), null when unlabeled. */
    incoherence: { type: Object, default: null },
    scoreMode: { type: String, default: 'computed_subtraction' },
  },

  computed: {
    funnel() {
      return computeFunnel(this.gestures, { scoreMode: this.scoreMode });
    },
    result() {
      return RESULT_PRESENTATION[resultVerdict(this.funnel)];
    },
    humanLabel() {
      return humanLabelText(this.incoherence);
    },
    durations() {
      return stageDurations(this.run);
    },
    delta() {
      if (!this.previousRun || !this.previousGestures) return null;
      return deltaVsPrevious(
        this.funnel,
        computeFunnel(this.previousGestures, { scoreMode: this.scoreMode })
      );
    },
    previousLabel() {
      return runLabel(this.previousRun);
    },
  },

  methods: {
    formatMs,
    formatDelta,
  },
};
</script>

<style scoped>
.run-summary-card .overline {
  line-height: 1.2rem;
}
</style>
